import React, { useState, useEffect } from 'react';
import {
  collection, addDoc, query, where, orderBy,
  getDocs, serverTimestamp, deleteDoc, doc
} from 'firebase/firestore';
import { db } from '../firebase';
import './SparesUsed.css';

export const CATEGORIES = [
  { value: 'seals',       label: '🔵 Seals & O-Rings'  },
  { value: 'valves',      label: '🔴 Valves'            },
  { value: 'pumps',       label: '⚙️ Pump Parts'        },
  { value: 'taps',        label: '🚰 Taps & Fittings'   },
  { value: 'hoses',       label: '🟡 Hoses & Tubing'    },
  { value: 'connectors',  label: '🔗 Connectors'        },
  { value: 'electrical',  label: '⚡ Electrical'         },
  { value: 'cleaning',    label: '🧹 Cleaning Supplies' },
  { value: 'other',       label: '📦 Other'             },
];

const emptyItem = { partName: '', category: 'seals', quantity: 1, unitCost: '', notes: '' };

function SparesUsed({ user, onBack }) {
  const [view, setView]               = useState('list');   // 'list' | 'add'
  const [spares, setSpares]           = useState([]);
  const [serviceRecords, setServiceRecords] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [filterDays, setFilterDays]   = useState(7);

  // Form state
  const [linkedService, setLinkedService] = useState('');
  const [items, setItems]             = useState([{ ...emptyItem }]);

  useEffect(() => { loadData(); }, [filterDays]);

  const loadData = async () => {
    setLoading(true);
    try {
      const since = new Date();
      since.setDate(since.getDate() - filterDays);
      since.setHours(0, 0, 0, 0);

      const [sparesSnap, svcSnap] = await Promise.all([
        getDocs(query(
          collection(db, 'sparesUsed'),
          where('userId', '==', user.id),
          where('timestamp', '>=', since.getTime()),
          orderBy('timestamp', 'desc')
        )),
        getDocs(query(
          collection(db, 'serviceRecords'),
          where('technicianId', '==', user.id),
          orderBy('createdAt', 'desc')
        ))
      ]);

      setSpares(sparesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setServiceRecords(svcSnap.docs.map(d => ({ id: d.id, ...d.data() })).slice(0, 30));
    } catch (e) {
      console.error('❌ Load spares error:', e);
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => setItems([...items, { ...emptyItem }]);

  const removeItem = (idx) => {
    if (items.length === 1) return;
    setItems(items.filter((_, i) => i !== idx));
  };

  const updateItem = (idx, field, value) => {
    setItems(items.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const handleSave = async () => {
    const valid = items.every(it => it.partName.trim() && it.quantity > 0);
    if (!valid) { alert('Please fill in all part names and quantities.'); return; }

    setSaving(true);
    try {
      const linkedSvc = serviceRecords.find(s => s.id === linkedService);

      const batch = items.map(item => {
        const unitCost = parseFloat(item.unitCost) || 0;
        return addDoc(collection(db, 'sparesUsed'), {
          userId:        user.id,
          userName:      user.name,
          userRole:      user.role,
          partName:      item.partName.trim(),
          category:      item.category,
          quantity:      parseInt(item.quantity),
          unitCost,
          totalCost:     unitCost * parseInt(item.quantity),
          notes:         item.notes.trim(),
          timestamp:     Date.now(),
          serverTimestamp: serverTimestamp(),
          // Linked service info
          serviceId:     linkedService || null,
          outletName:    linkedSvc?.outletName || '',
          orderNumber:   linkedSvc?.orderNumber || '',
          serviceType:   linkedSvc?.serviceType || '',
        });
      });

      await Promise.all(batch);
      setItems([{ ...emptyItem }]);
      setLinkedService('');
      setView('list');
      await loadData();
      showToast(`✅ ${items.length} spare${items.length > 1 ? 's' : ''} recorded!`);
    } catch (e) {
      console.error('❌ Save spares error:', e);
      alert('❌ Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (spare) => {
    if (!window.confirm(`Remove "${spare.partName}" from records?`)) return;
    try {
      await deleteDoc(doc(db, 'sparesUsed', spare.id));
      await loadData();
      showToast('🗑️ Spare removed.');
    } catch (e) {
      console.error('❌ Delete error:', e);
    }
  };

  const getTotals = () => {
    const totalItems = spares.reduce((acc, s) => acc + s.quantity, 0);
    const totalCost  = spares.reduce((acc, s) => acc + (s.totalCost || 0), 0);
    return { totalItems, totalCost };
  };

  const getCatInfo = (val) => CATEGORIES.find(c => c.value === val) || CATEGORIES[8];

  const formatDate = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleDateString('en-KE', { day: 'numeric', month: 'short' });
  };

  const showToast = (msg) => {
    document.querySelectorAll('.toast-notification').forEach(t => t.remove());
    const t = document.createElement('div');
    t.className = 'toast-notification';
    t.innerHTML = `<div class="toast-icon">🔩</div><div class="toast-message">${msg}</div>`;
    document.body.appendChild(t);
    setTimeout(() => t.remove(), 3000);
  };

  const totals = getTotals();

  // ── ADD FORM ─────────────────────────────────────────────
  if (view === 'add') {
    return (
      <div className="spares-page">
        <div className="spares-header">
          <button onClick={() => { setView('list'); setItems([{ ...emptyItem }]); }} className="back-button">← Back</button>
          <h1>🔩 Log Spares Used</h1>
        </div>

        <div className="spares-form-card">
          {/* Link to service */}
          <div className="spares-form-section">
            <div className="spares-section-title">🔗 Link to Service Record (Optional)</div>
            <select className="form-input" value={linkedService}
              onChange={e => setLinkedService(e.target.value)}>
              <option value="">— Not linked to a specific job —</option>
              {serviceRecords.map(s => (
                <option key={s.id} value={s.id}>
                  #{s.orderNumber || 'N/A'} · {s.outletName} · {formatDate(s.createdAt?.toDate?.() || s.createdAt)}
                </option>
              ))}
            </select>
          </div>

          {/* Parts list */}
          <div className="spares-form-section">
            <div className="spares-section-title-row">
              <div className="spares-section-title">📦 Parts Used</div>
              <button className="add-part-btn" onClick={addItem}>+ Add Part</button>
            </div>

            {items.map((item, idx) => (
              <div key={idx} className="part-item-card">
                <div className="part-item-header">
                  <span className="part-number">Part {idx + 1}</span>
                  {items.length > 1 && (
                    <button className="remove-part-btn" onClick={() => removeItem(idx)}>✕ Remove</button>
                  )}
                </div>

                <div className="form-group">
                  <label>Part Name *</label>
                  <input className="form-input" placeholder="e.g. Keg coupler seal, Ball valve"
                    value={item.partName} onChange={e => updateItem(idx, 'partName', e.target.value)} />
                </div>

                <div className="form-row-3">
                  <div className="form-group">
                    <label>Category</label>
                    <select className="form-input" value={item.category}
                      onChange={e => updateItem(idx, 'category', e.target.value)}>
                      {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Qty *</label>
                    <input className="form-input" type="number" min="1" value={item.quantity}
                      onChange={e => updateItem(idx, 'quantity', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Unit Cost (KSh)</label>
                    <input className="form-input" type="number" min="0" placeholder="0"
                      value={item.unitCost} onChange={e => updateItem(idx, 'unitCost', e.target.value)} />
                  </div>
                </div>

                {item.unitCost > 0 && (
                  <div className="part-total">
                    Total: <strong>KSh {(parseFloat(item.unitCost || 0) * parseInt(item.quantity || 1)).toLocaleString()}</strong>
                  </div>
                )}

                <div className="form-group">
                  <label>Notes</label>
                  <input className="form-input" placeholder="Optional note..."
                    value={item.notes} onChange={e => updateItem(idx, 'notes', e.target.value)} />
                </div>
              </div>
            ))}
          </div>

          {/* Grand total */}
          {items.some(i => parseFloat(i.unitCost) > 0) && (
            <div className="grand-total-row">
              <span>Grand Total</span>
              <strong>KSh {items.reduce((acc, i) =>
                acc + (parseFloat(i.unitCost || 0) * parseInt(i.quantity || 1)), 0
              ).toLocaleString()}</strong>
            </div>
          )}

          <div className="form-actions-row">
            <button className="cancel-btn"
              onClick={() => { setView('list'); setItems([{ ...emptyItem }]); }}>
              Cancel
            </button>
            <button className="submit-btn" onClick={handleSave} disabled={saving}>
              {saving
                ? <><span className="spinner-small"></span> Saving...</>
                : `💾 Save ${items.length} Spare${items.length > 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── LIST VIEW ─────────────────────────────────────────────
  return (
    <div className="spares-page">
      <div className="spares-header">
        <button onClick={onBack} className="back-button">← Back</button>
        <div className="spares-header-text">
          <h1>🔩 Spares Used</h1>
          <p>{spares.length} entries · last {filterDays} days</p>
        </div>
        <button className="log-spare-btn" onClick={() => setView('add')}>+ Log Spares</button>
      </div>

      {/* Summary cards */}
      <div className="spares-summary">
        <div className="spares-summary-card">
          <div className="spares-summary-val">{totals.totalItems}</div>
          <div className="spares-summary-lbl">Parts Used</div>
        </div>
        <div className="spares-summary-card">
          <div className="spares-summary-val">{spares.length}</div>
          <div className="spares-summary-lbl">Log Entries</div>
        </div>
        <div className="spares-summary-card highlight">
          <div className="spares-summary-val">KSh {totals.totalCost.toLocaleString()}</div>
          <div className="spares-summary-lbl">Total Cost</div>
        </div>
      </div>

      {/* Filter pills */}
      <div className="filter-pills">
        {[7, 14, 30].map(d => (
          <button key={d}
            className={`filter-pill ${filterDays === d ? 'active' : ''}`}
            onClick={() => setFilterDays(d)}>
            Last {d} days
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading-state"><div className="spinner"></div><p>Loading spares...</p></div>
      ) : spares.length === 0 ? (
        <div className="spares-empty">
          <div className="empty-icon">🔩</div>
          <h3>No spares logged yet</h3>
          <p>Tap "Log Spares" to record parts used on a job</p>
          <button className="log-spare-btn-lg" onClick={() => setView('add')}>+ Log Spares</button>
        </div>
      ) : (
        <div className="spares-list">
          {spares.map(spare => {
            const cat = getCatInfo(spare.category);
            return (
              <div key={spare.id} className="spare-card">
                <div className="spare-card-left">
                  <div className="spare-cat-icon">{cat.label.split(' ')[0]}</div>
                </div>
                <div className="spare-card-body">
                  <div className="spare-name">{spare.partName}</div>
                  <div className="spare-meta">
                    <span className="spare-cat-badge">{cat.label}</span>
                    {spare.outletName && <span className="spare-outlet">📍 {spare.outletName}</span>}
                  </div>
                  {spare.notes && <div className="spare-notes">💬 {spare.notes}</div>}
                </div>
                <div className="spare-card-right">
                  <div className="spare-qty">×{spare.quantity}</div>
                  {spare.totalCost > 0 && (
                    <div className="spare-cost">KSh {spare.totalCost.toLocaleString()}</div>
                  )}
                  <div className="spare-date">{formatDate(spare.timestamp)}</div>
                  <button className="spare-delete-btn" onClick={() => handleDelete(spare)}>🗑️</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default SparesUsed;
